import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Cpu, GitBranch, Clock, Tag, Plus, Play, Pause, WrenchIcon, AlertOctagon } from "lucide-react";

export function MfgMastersPanel() {
  return (
    <Card className="border-[#EAE5D9] bg-white" data-testid="mfg-masters-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-[Outfit] flex items-center gap-2"><Cpu className="w-4 h-4 text-purple-700" /> Manufacturing Masters</CardTitle>
        <p className="text-xs text-[#8B7355]">Production lines · Machines · Shifts · Brands — the foundation of the MES.</p>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="lines">
          <TabsList className="bg-[#FAF6EE] flex-wrap h-auto">
            <TabsTrigger value="lines" data-testid="mfg-tab-lines"><GitBranch className="w-3 h-3 mr-1" /> Lines</TabsTrigger>
            <TabsTrigger value="machines" data-testid="mfg-tab-machines"><Cpu className="w-3 h-3 mr-1" /> Machines</TabsTrigger>
            <TabsTrigger value="shifts" data-testid="mfg-tab-shifts"><Clock className="w-3 h-3 mr-1" /> Shifts</TabsTrigger>
            <TabsTrigger value="brands" data-testid="mfg-tab-brands"><Tag className="w-3 h-3 mr-1" /> Brands</TabsTrigger>
          </TabsList>
          <TabsContent value="lines"><LinesTab /></TabsContent>
          <TabsContent value="machines"><MachinesTab /></TabsContent>
          <TabsContent value="shifts"><ShiftsTab /></TabsContent>
          <TabsContent value="brands"><BrandsTab /></TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

// -------- Common branch selector --------
function useBranches() {
  const [branches, setBranches] = useState([]);
  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);
  return branches;
}

// ============ LINES ============
function LinesTab() {
  const branches = useBranches();
  const [rows, setRows] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    const q = branchFilter ? `?branch_id=${branchFilter}` : "";
    try { const r = await api.get(`/mfg/lines${q}`); setRows(r.data); } catch { /* */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchFilter]);

  const save = async () => {
    if (!form.branch_id || !form.name) { toast.error("Factory + Name required"); return; }
    try {
      const payload = { ...form, capacity_per_hour: form.capacity_per_hour ? Number(form.capacity_per_hour) : null };
      if (form.id) await api.patch(`/mfg/lines/${form.id}`, payload); else await api.post("/mfg/lines", payload);
      toast.success("Saved"); setOpen(false); setForm({}); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete line?")) return;
    try { await api.delete(`/mfg/lines/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const branchName = (id) => (branches.find((b) => b.id === id) || {}).name || id;

  return (
    <div className="pt-3 space-y-3">
      <div className="flex justify-between items-center gap-2">
        <Select value={branchFilter} onValueChange={(v) => setBranchFilter(v === "__all" ? "" : v)}>
          <SelectTrigger className="w-72" data-testid="mfg-lines-branch-filter"><SelectValue placeholder="Filter by factory" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="__all">All factories</SelectItem>
            {branches.filter((b) => b.type === "factory").map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" className="bg-[#C05621] text-white" onClick={() => { setForm({}); setOpen(true); }} data-testid="mfg-line-new"><Plus className="w-3 h-3 mr-1" /> New Line</Button>
      </div>
      <div className="border border-[#EAE5D9] rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="mfg-lines-rows">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-[11px] uppercase">
            <tr><th className="text-left px-3 py-2">Factory</th><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Code</th><th className="text-right px-3 py-2">Cap/hr</th><th className="text-center px-3 py-2">Active</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#EAE5D9]">
                <td className="px-3 py-2 text-xs">{branchName(r.branch_id)}</td>
                <td className="px-3 py-2">{r.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.code || "—"}</td>
                <td className="px-3 py-2 text-right">{r.capacity_per_hour ?? "—"}</td>
                <td className="px-3 py-2 text-center">{r.active ? <Badge className="bg-emerald-100 text-emerald-800 border-0">yes</Badge> : <Badge className="bg-slate-100 text-slate-700 border-0">no</Badge>}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => { setForm(r); setOpen(true); }}>Edit</Button>
                  <Button size="sm" variant="ghost" className="text-red-600 ml-1" onClick={() => del(r.id)}>×</Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-[#8B7355]">No lines yet — click <b>New Line</b> to add.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-[Outfit]">{form.id ? "Edit" : "New"} Production Line</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div className="col-span-2"><Label>Factory*</Label>
              <Select value={form.branch_id || ""} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger data-testid="mfg-line-form-branch"><SelectValue placeholder="Select factory" /></SelectTrigger>
                <SelectContent className="bg-white">{branches.filter((b) => b.type === "factory").map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Name*</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Line A - Cakes" data-testid="mfg-line-form-name" /></div>
            <div><Label>Code</Label><Input value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="LINE_A" data-testid="mfg-line-form-code" /></div>
            <div><Label>Capacity per hour</Label><Input type="number" value={form.capacity_per_hour || ""} onChange={(e) => setForm({ ...form, capacity_per_hour: e.target.value })} data-testid="mfg-line-form-cap" /></div>
            <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-[#2C1810] text-white" data-testid="mfg-line-form-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ MACHINES ============
function MachinesTab() {
  const branches = useBranches();
  const [rows, setRows] = useState([]);
  const [lines, setLines] = useState([]);
  const [summary, setSummary] = useState({ running: 0, idle: 0, breakdown: 0, maintenance: 0, total: 0 });
  const [branchFilter, setBranchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(null);
  const [statusForm, setStatusForm] = useState({});
  const [form, setForm] = useState({});

  const load = async () => {
    const params = new URLSearchParams();
    if (branchFilter) params.set("branch_id", branchFilter);
    if (statusFilter) params.set("status", statusFilter);
    const q = params.toString() ? `?${params.toString()}` : "";
    try {
      const [r, s, l] = await Promise.all([
        api.get(`/mfg/machines${q}`),
        api.get(`/mfg/machines/summary${branchFilter ? `?branch_id=${branchFilter}` : ""}`),
        api.get(`/mfg/lines${branchFilter ? `?branch_id=${branchFilter}` : ""}`),
      ]);
      setRows(r.data); setSummary(s.data); setLines(l.data);
    } catch { /* */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchFilter, statusFilter]);

  const save = async () => {
    if (!form.branch_id || !form.name) { toast.error("Factory + Name required"); return; }
    try {
      if (form.id) await api.patch(`/mfg/machines/${form.id}`, form); else await api.post("/mfg/machines", form);
      toast.success("Saved"); setOpen(false); setForm({}); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete machine?")) return;
    try { await api.delete(`/mfg/machines/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const commitStatus = async () => {
    if (!statusOpen || !statusForm.status) return;
    try {
      await api.post(`/mfg/machines/${statusOpen}/status`, statusForm);
      toast.success("Status updated"); setStatusOpen(null); setStatusForm({}); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const branchName = (id) => (branches.find((b) => b.id === id) || {}).name || id;
  const lineName = (id) => (lines.find((l) => l.id === id) || {}).name || "—";
  const statusColor = (s) => s === "running" ? "bg-emerald-100 text-emerald-800" : s === "idle" ? "bg-slate-100 text-slate-700" : s === "breakdown" ? "bg-red-100 text-red-800" : s === "maintenance" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700";

  return (
    <div className="pt-3 space-y-3">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="mfg-machine-summary">
        <SummaryTile label="Total" value={summary.total} tone="neutral" icon={Cpu} />
        <SummaryTile label="Running" value={summary.running} tone="green" icon={Play} />
        <SummaryTile label="Idle" value={summary.idle} tone="slate" icon={Pause} />
        <SummaryTile label="Breakdown" value={summary.breakdown} tone="red" icon={AlertOctagon} />
        <SummaryTile label="Maintenance" value={summary.maintenance} tone="amber" icon={WrenchIcon} />
      </div>
      <div className="flex gap-2 items-center flex-wrap">
        <Select value={branchFilter} onValueChange={(v) => setBranchFilter(v === "__all" ? "" : v)}>
          <SelectTrigger className="w-56" data-testid="mfg-machines-branch-filter"><SelectValue placeholder="Factory" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="__all">All factories</SelectItem>{branches.filter((b) => b.type === "factory").map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === "__all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="mfg-machines-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="__all">All statuses</SelectItem>
            {["running", "idle", "breakdown", "maintenance"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" className="bg-[#C05621] text-white ml-auto" onClick={() => { setForm({ status: "idle", active: true }); setOpen(true); }} data-testid="mfg-machine-new"><Plus className="w-3 h-3 mr-1" /> New Machine</Button>
      </div>
      <div className="border border-[#EAE5D9] rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="mfg-machines-rows">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-[11px] uppercase">
            <tr><th className="text-left px-3 py-2">Factory</th><th className="text-left px-3 py-2">Line</th><th className="text-left px-3 py-2">Machine</th><th className="text-left px-3 py-2">Type</th><th className="text-center px-3 py-2">Status</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#EAE5D9]">
                <td className="px-3 py-2 text-xs">{branchName(r.branch_id)}</td>
                <td className="px-3 py-2 text-xs">{lineName(r.line_id)}</td>
                <td className="px-3 py-2"><div className="font-semibold">{r.name}</div><div className="text-[10px] text-[#8B7355]">{r.code || "—"} · {r.make_model || ""}</div></td>
                <td className="px-3 py-2 text-xs">{r.type || "—"}</td>
                <td className="px-3 py-2 text-center"><Badge className={`border-0 ${statusColor(r.status)}`}>{r.status}</Badge></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => { setStatusOpen(r.id); setStatusForm({ status: r.status }); }} data-testid={`mfg-machine-status-btn-${r.id}`}>Set Status</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setForm(r); setOpen(true); }} className="ml-1">Edit</Button>
                  <Button size="sm" variant="ghost" className="text-red-600 ml-1" onClick={() => del(r.id)}>×</Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-[#8B7355]">No machines yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Create/edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-[Outfit]">{form.id ? "Edit" : "New"} Machine</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div><Label>Factory*</Label>
              <Select value={form.branch_id || ""} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger data-testid="mfg-machine-form-branch"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{branches.filter((b) => b.type === "factory").map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Line</Label>
              <Select value={form.line_id || ""} onValueChange={(v) => setForm({ ...form, line_id: v === "__none" ? null : v })}>
                <SelectTrigger data-testid="mfg-machine-form-line"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="__none">Unassigned</SelectItem>{lines.filter((l) => !form.branch_id || l.branch_id === form.branch_id).map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Name*</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Mixer M1" data-testid="mfg-machine-form-name" /></div>
            <div><Label>Code</Label><Input value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="MX-01" /></div>
            <div><Label>Type</Label>
              <Select value={form.type || ""} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent className="bg-white">{["mixer", "oven", "cooling", "packing", "metal_detector", "conveyor", "other"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Make / Model</Label><Input value={form.make_model || ""} onChange={(e) => setForm({ ...form, make_model: e.target.value })} placeholder="Hobart HL662" /></div>
            <div><Label>Installed at</Label><Input type="date" value={(form.installed_at || "").slice(0, 10)} onChange={(e) => setForm({ ...form, installed_at: e.target.value })} /></div>
            <div><Label>Next service due</Label><Input type="date" value={(form.next_service_due || "").slice(0, 10)} onChange={(e) => setForm({ ...form, next_service_due: e.target.value })} /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-[#2C1810] text-white" data-testid="mfg-machine-form-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set status dialog */}
      <Dialog open={!!statusOpen} onOpenChange={(o) => !o && setStatusOpen(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-[Outfit]">Set Machine Status</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 pt-2">
            <div><Label>Status*</Label>
              <Select value={statusForm.status || ""} onValueChange={(v) => setStatusForm({ ...statusForm, status: v })}>
                <SelectTrigger data-testid="mfg-machine-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{["running", "idle", "breakdown", "maintenance"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Reason / batch note</Label><Input value={statusForm.reason || ""} onChange={(e) => setStatusForm({ ...statusForm, reason: e.target.value })} placeholder="Batch#42 started / bearing failure / weekly PM" data-testid="mfg-machine-status-reason" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setStatusOpen(null)}>Cancel</Button><Button onClick={commitStatus} className="bg-emerald-700 text-white" data-testid="mfg-machine-status-save">Update</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryTile({ label, value, tone = "neutral", icon: Icon }) {
  const bg = { green: "bg-emerald-50 border-emerald-200 text-emerald-700", red: "bg-red-50 border-red-200 text-red-700", amber: "bg-amber-50 border-amber-200 text-amber-700", slate: "bg-slate-50 border-slate-200 text-slate-700", neutral: "bg-white border-[#EAE5D9] text-[#2C1810]" }[tone];
  return (
    <div className={`border rounded-md p-3 ${bg}`}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-2xl font-[Outfit] font-semibold mt-0.5">{value ?? 0}</div>
    </div>
  );
}

// ============ SHIFTS ============
function ShiftsTab() {
  const branches = useBranches();
  const [rows, setRows] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    const q = branchFilter ? `?branch_id=${branchFilter}` : "";
    try { const r = await api.get(`/mfg/shifts${q}`); setRows(r.data); } catch { /* */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchFilter]);

  const save = async () => {
    if (!form.branch_id || !form.name || !form.start_time || !form.end_time) { toast.error("Factory + Name + Start + End required"); return; }
    try {
      if (form.id) await api.patch(`/mfg/shifts/${form.id}`, form); else await api.post("/mfg/shifts", form);
      toast.success("Saved"); setOpen(false); setForm({}); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete shift?")) return; try { await api.delete(`/mfg/shifts/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const seedDefaults = async () => {
    if (!branchFilter) { toast.error("Select a factory first"); return; }
    try { const r = await api.post(`/mfg/shifts/seed-defaults?branch_id=${branchFilter}`); toast.success(`Seeded ${r.data.inserted} default shift(s)`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const branchName = (id) => (branches.find((b) => b.id === id) || {}).name || id;

  return (
    <div className="pt-3 space-y-3">
      <div className="flex gap-2 items-center">
        <Select value={branchFilter} onValueChange={(v) => setBranchFilter(v === "__all" ? "" : v)}>
          <SelectTrigger className="w-72" data-testid="mfg-shifts-branch-filter"><SelectValue placeholder="Filter by factory" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="__all">All factories</SelectItem>{branches.filter((b) => b.type === "factory").map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={seedDefaults} disabled={!branchFilter} data-testid="mfg-shifts-seed">Seed 3 defaults</Button>
        <Button size="sm" className="bg-[#C05621] text-white ml-auto" onClick={() => { setForm({ branch_id: branchFilter || undefined, active: true }); setOpen(true); }} data-testid="mfg-shift-new"><Plus className="w-3 h-3 mr-1" /> New Shift</Button>
      </div>
      <div className="border border-[#EAE5D9] rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="mfg-shifts-rows">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-[11px] uppercase"><tr><th className="text-left px-3 py-2">Factory</th><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Code</th><th className="text-center px-3 py-2">Start</th><th className="text-center px-3 py-2">End</th><th className="text-center px-3 py-2">Active</th><th className="px-3 py-2"></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#EAE5D9]">
                <td className="px-3 py-2 text-xs">{branchName(r.branch_id)}</td>
                <td className="px-3 py-2">{r.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.code || "—"}</td>
                <td className="px-3 py-2 text-center font-mono text-xs">{r.start_time}</td>
                <td className="px-3 py-2 text-center font-mono text-xs">{r.end_time}</td>
                <td className="px-3 py-2 text-center">{r.active ? <Badge className="bg-emerald-100 text-emerald-800 border-0">yes</Badge> : <Badge className="bg-slate-100 text-slate-700 border-0">no</Badge>}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap"><Button size="sm" variant="outline" onClick={() => { setForm(r); setOpen(true); }}>Edit</Button><Button size="sm" variant="ghost" className="text-red-600 ml-1" onClick={() => del(r.id)}>×</Button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-[#8B7355]">No shifts — pick a factory and click <b>Seed 3 defaults</b>.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-[Outfit]">{form.id ? "Edit" : "New"} Shift</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div className="col-span-2"><Label>Factory*</Label>
              <Select value={form.branch_id || ""} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger data-testid="mfg-shift-form-branch"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{branches.filter((b) => b.type === "factory").map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Name*</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Morning" data-testid="mfg-shift-form-name" /></div>
            <div><Label>Code</Label><Input value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="MOR" /></div>
            <div><Label>Start*</Label><Input type="time" value={form.start_time || ""} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="mfg-shift-form-start" /></div>
            <div><Label>End*</Label><Input type="time" value={form.end_time || ""} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="mfg-shift-form-end" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-[#2C1810] text-white" data-testid="mfg-shift-form-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ BRANDS ============
function BrandsTab() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    try { const [r, c] = await Promise.all([api.get("/mfg/brands"), api.get("/customers")]); setRows(r.data); setCustomers(c.data); } catch { /* */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    if (form.is_private_label && !form.customer_id) { toast.error("Pick the private-label customer"); return; }
    try {
      const payload = { ...form, customer_id: form.is_private_label ? form.customer_id : null };
      if (form.id) await api.patch(`/mfg/brands/${form.id}`, payload); else await api.post("/mfg/brands", payload);
      toast.success("Saved"); setOpen(false); setForm({}); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete brand?")) return; try { await api.delete(`/mfg/brands/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const custName = (id) => (customers.find((c) => c.id === id) || {}).name || "—";

  return (
    <div className="pt-3 space-y-3">
      <div className="flex justify-between items-center">
        <div className="text-xs text-[#8B7355]">Brands power private-label tracking, brand-wise reports and executive dashboards.</div>
        <Button size="sm" className="bg-[#C05621] text-white" onClick={() => { setForm({ active: true }); setOpen(true); }} data-testid="mfg-brand-new"><Plus className="w-3 h-3 mr-1" /> New Brand</Button>
      </div>
      <div className="border border-[#EAE5D9] rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="mfg-brands-rows">
          <thead className="bg-[#FAF6EE] text-[#8B7355] text-[11px] uppercase"><tr><th className="text-left px-3 py-2">Brand</th><th className="text-left px-3 py-2">Code</th><th className="text-center px-3 py-2">Private Label</th><th className="text-left px-3 py-2">Customer</th><th className="text-right px-3 py-2">Products</th><th className="text-center px-3 py-2">Active</th><th className="px-3 py-2"></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#EAE5D9]">
                <td className="px-3 py-2 font-semibold">{r.name}<div className="text-[10px] text-[#8B7355]">{r.description || ""}</div></td>
                <td className="px-3 py-2 font-mono text-xs">{r.code || "—"}</td>
                <td className="px-3 py-2 text-center">{r.is_private_label ? <Badge className="bg-blue-100 text-blue-800 border-0">private</Badge> : <Badge className="bg-slate-100 text-slate-700 border-0">own</Badge>}</td>
                <td className="px-3 py-2 text-xs">{r.customer_id ? custName(r.customer_id) : "—"}</td>
                <td className="px-3 py-2 text-right">{r.product_count ?? 0}</td>
                <td className="px-3 py-2 text-center">{r.active ? <Badge className="bg-emerald-100 text-emerald-800 border-0">yes</Badge> : <Badge className="bg-slate-100 text-slate-700 border-0">no</Badge>}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap"><Button size="sm" variant="outline" onClick={() => { setForm(r); setOpen(true); }}>Edit</Button><Button size="sm" variant="ghost" className="text-red-600 ml-1" onClick={() => del(r.id)}>×</Button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-[#8B7355]">No brands yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-[Outfit]">{form.id ? "Edit" : "New"} Brand</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div><Label>Name*</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Bakemill Signature" data-testid="mfg-brand-form-name" /></div>
            <div><Label>Code</Label><Input value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="BM_SIG" /></div>
            <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Logo URL</Label><Input value={form.logo_url || ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} /></div>
            <div className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={!!form.is_private_label} onChange={(e) => setForm({ ...form, is_private_label: e.target.checked })} className="w-4 h-4" data-testid="mfg-brand-form-private" />
              <span className="text-sm">Private-label brand</span>
            </div>
            {form.is_private_label && (
              <div className="col-span-2"><Label>Customer*</Label>
                <Select value={form.customer_id || ""} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger data-testid="mfg-brand-form-customer"><SelectValue placeholder="Pick private-label customer" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72 overflow-y-auto">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.company ? `· ${c.company}` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-[#2C1810] text-white" data-testid="mfg-brand-form-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
